import { Component, Input, OnInit } from "@angular/core";
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from "@angular/forms";
import { DateTime } from "luxon";
import { DatePickerModule } from "primeng/datepicker";
import { DatepickerMaskDirective } from "../../../../../shared/directives/datepicker-mask/datepicker-mask.directive";
import { EventDatesGroup } from "../../event-form.form";

type EventDatesForm = FormGroup<EventDatesGroup>;

@Component({
  selector: "app-event-date-step",
  imports: [ReactiveFormsModule, DatePickerModule, DatepickerMaskDirective],
  templateUrl: "./event-date-step.component.html",
  styleUrl: "./event-date-step.component.scss",
})
export class EventDateStepComponent implements OnInit {
  @Input({ required: true }) formGroup!: EventDatesForm;

  @Input() allowPastDates = false;

  dateNow = new Date();

  ngOnInit(): void {
    this.ensureValidators();
  }

  get startDateTime(): FormControl<Date | null> {
    return this.formGroup.controls.startDateTime;
  }

  get endDateTime(): FormControl<Date | null> {
    return this.formGroup.controls.endDateTime;
  }

  hasControlError(control: keyof EventDatesGroup, error: string): boolean {
    const c = this.formGroup.controls[control];
    return c.touched && c.hasError(error);
  }

  hasGroupError(
    error: "dateBeforeCurrent" | "datesAreEqual" | "dateEndBeforeStart",
  ): boolean {
    return this.formGroup.touched && this.formGroup.hasError(error);
  }

  // TODO: verificar essa merda aqui
  private ensureValidators(): void {
    // validators de campo
    this.startDateTime.addValidators(Validators.required);
    this.endDateTime.addValidators(Validators.required);

    // validators de grupo (regras do legado)
    this.formGroup.setValidators([
      eventDateRangeValidator({ allowPastDates: this.allowPastDates }),
    ]);

    // garante revalidação inicial
    this.startDateTime.updateValueAndValidity({ emitEvent: false });
    this.endDateTime.updateValueAndValidity({ emitEvent: false });
    this.formGroup.updateValueAndValidity({ emitEvent: false });
  }
}

// TODO: verificar essa merda aqui
/**
 * Regras:
 * - dateBeforeCurrent: start ou end antes de "agora" (configurável)
 * - datesAreEqual: start === end
 * - dateEndBeforeStart: end < start
 */
export function eventDateRangeValidator(opts?: {
  allowPastDates?: boolean;
}): ValidatorFn {
  const allowPastDates = !!opts?.allowPastDates;

  return (control: AbstractControl): ValidationErrors | null => {
    const fg = control as EventDatesForm;
    if (!fg?.controls) return null;

    const { startDateTime, endDateTime } = fg.controls;

    // se ainda não tem tudo preenchido, não acusa erro de range
    if (!startDateTime.value || !endDateTime.value) {
      return null;
    }

    const start = toLuxon(startDateTime.value);
    const end = toLuxon(endDateTime.value);

    if (!start || !end) return null;

    const errors: ValidationErrors = {};

    if (end.toMillis() < start.toMillis()) {
      errors["dateEndBeforeStart"] = true;
    }

    if (end.toMillis() === start.toMillis()) {
      errors["datesAreEqual"] = true;
    }

    if (!allowPastDates) {
      const now = DateTime.local();
      if (
        start.toMillis() < now.toMillis() ||
        end.toMillis() < now.toMillis()
      ) {
        errors["dateBeforeCurrent"] = true;
      }
    }

    return Object.keys(errors).length ? errors : null;
  };
}

function toLuxon(date: Date | string | null | undefined): DateTime | null {
  if (!date) return null;

  const dt =
    date instanceof Date ? DateTime.fromJSDate(date) : DateTime.fromISO(date); // string

  return dt.isValid ? dt : null;
}
